// uses your TCSelect
import { Grid } from "@mui/material";
import { TCSelect } from "components/TopCourtComponents/Input/TopCourtInput";
import React, { useMemo, useState, useEffect } from "react";

/**
 * props:
 * - participants: [{ value, label }, ...]
 * - pools: your array (with {_id, name, index, seeds:[{_id, participants}]})
 * - onAssign?: ({ poolId, seedId, option }) => void  // option can be null
 */
export default function DynamicPoolsGrid({ participants, pools, onAssign, disabled }) {
  // ---- build initial selections per seedId from pools.seeds[].participants ----
  const initialBySeed = useMemo(() => {
    const byId = {};
    pools.forEach(p => {
      (p.seeds || []).forEach(s => {
        const sp = s.participants; // could be null | id | { _id, name }
        let initial = null;
        if (typeof sp === "string") {
          initial = participants.find(o => o.value === sp) || null;
        } else if (sp && typeof sp === "object" && sp._id) {
          initial =
            participants.find(o => o.value === sp._id) ||
            { value: sp._id, label: sp.name || sp.label || "Selected" };
        }
        byId[s._id] = initial;
      });
    });
    return byId;
  }, [pools, participants]);

  // selections keyed by seedId
  const [selections, setSelections] = useState(initialBySeed);

  // keep keys in sync when pools change
  useEffect(() => {
    const next = { ...selections };
    const allSeedIds = pools.flatMap(p => p.seeds?.map(s => s._id) || []);
    // add missing seeds
    allSeedIds.forEach(id => {
      if (!(id in next)) next[id] = initialBySeed[id] ?? null;
    });
    // drop removed
    Object.keys(next).forEach(id => {
      if (!allSeedIds.includes(id)) delete next[id];
    });
    setSelections(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pools, initialBySeed]);

  // prevent duplicates across ALL pools (except the current cell)
  const chosen = useMemo(() => {
    const s = new Set();
    Object.values(selections).forEach(opt => opt && s.add(opt.value));
    return s;
  }, [selections]);

  const optionsFor = (seedId) => {
    const current = selections[seedId]?.value;
    return participants.filter(o => !chosen.has(o.value) || o.value === current);
  };

  const handleChange = (poolId, seedId, option) => {
    setSelections(prev => ({ ...prev, [seedId]: option || null }));
    onAssign && onAssign({ poolId, seedId, option: option || null });
  };

  // sort pools by index asc (like your data)
  const sortedPools = [...pools].sort((a, b) => (a.index || 0) - (b.index || 0));

  return (
    <div style={wrapStyles.grid}>
      {sortedPools.map(p => {
        const seeds = p.seeds || [];
        const half = Math.ceil(seeds.length / 2); // left takes first half
        const left  = seeds.slice(0, half);
        const right = seeds.slice(half);
        const poolSuffix = p.name?.split(" ").pop() || "";
        return (
          <section key={p._id} style={wrapStyles.card}>
            <header style={wrapStyles.header}>{p.label}</header>
            <Grid container xs={12} spacing={1} padding={1} pt={0}>
              <Grid item xs={6}>
                {left.map(( seed, i ) => (
                  <div key={seed._id} style={wrapStyles.row}>
                    <span style={wrapStyles.seedLabel}>
                      {`Seed ${poolSuffix} ${i + 1}`}
                    </span>
                    <div style={{ width: "100%", marginLeft: 8 }}>
                      <TCSelect
                        options={optionsFor(seed._id)}
                        value={selections[seed._id] || null}
                        onChange={(opt) => handleChange(p._id, seed._id, opt || null)}
                        placeholder="Select participant…"
                        isClearable
                        disabled={disabled}
                      />
                    </div>
                  </div>
                ))}
              </Grid>

              <Grid item xs={6}>
                {right.map((seed, i ) => (
                  <div key={seed._id} style={wrapStyles.row}>
                    <span style={wrapStyles.seedLabel}>
                      {`Seed ${poolSuffix} ${half + (i + 1)}`}
                    </span>
                    <div style={{ width: "100%", marginLeft: 8 }}>
                      <TCSelect
                        options={optionsFor(seed._id)}
                        value={selections[seed._id] || null}
                        onChange={(opt) => handleChange(p._id, seed._id, opt || null)}
                        placeholder="Select participant…"
                        isClearable
                        disabled={disabled}
                      />
                    </div>
                  </div>
                ))}
              </Grid>
            </Grid>
          </section>
        )
      })}
    </div>
  );
}

/* ---- Layout styles ---- */
const wrapStyles = {
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", // wraps to new row
    gap: 16,
  },
  card: {
    border: "1px solid #D1D5DB",
    borderRadius: 8,
    overflow: "hidden",
    background: "#fff",
  },
  header: {
    padding: "10px 12px",
    fontWeight: 600,
    background: "#F9FAFB",
    borderBottom: "1px solid #E5E7EB",
  },
  rows: { padding: 12, display: "grid", gap: 10 },
  row: {
    display: "flex",                 // ← was missing
    // gridTemplateColumns: "150px 1fr",
    alignItems: "center",
    marginTop: 8
  },
  seedLabel: { color: "#374151", whiteSpace: "nowrap" },
};